import * as THREE from "three";
import type { World as SceneWorld } from "./scene";
import type { CatBoneCapsules } from "./cat-physics";
import {
  type Behavior,
  type CatKeyInput,
  type CatPose,
  FADE,
  FLOOR_Y,
  INITIAL_HEADING,
  KEY_TURN_RATE,
  MEDIUM_TURN,
  POSE_IDLE,
  POSE_TRANSITIONS,
  SMALL_TURN,
  TURN_CLIPS,
  WAYPOINT_ARRIVE_RADIUS,
  WAYPOINT_TROT_DISTANCE,
  WAYPOINT_TURN_THRESHOLD,
  X_MAX,
  X_MIN,
  Z_MAX,
  Z_MIN,
} from "./cat-config";
import {
  behaviorDuration,
  clampToBounds,
  outOfBounds,
  pickWeighted,
  planTransitions,
  wrapAngle,
} from "./cat-utils";

// Animator: owns the cat's animation plumbing — mixer, AnimationActions,
// the four-mode state machine (playing / turning / transitioning_pose /
// controlled), heading + model.rotation.y, the per-frame dispatcher, and
// every helper that mutates them. The orchestrating cat.ts module loads
// the GLB, builds the action pool, and feeds two AI-policy callbacks
// (`pickNext` / `pickRedirect`) that the animator invokes when a
// behavior's duration expires or locomotion crosses the roam-zone bound.
//
// Why split this out: cat.ts had grown to 1100+ lines mixing animation
// plumbing with behavior policy. Each was independently understandable
// but the file as a whole had to be read end-to-end to find anything.
// The split lines up with the natural seam: animator = "what the cat is
// doing right now and how the clips advance," controller (cat.ts) =
// "what the cat *should* be doing next."

export type Mode = "playing" | "turning" | "transitioning_pose" | "controlled";

/** Decision returned by the AI-policy callbacks. The animator translates
 * each variant into the right concrete trigger (transition, pose
 * transition, or turn). */
export type NextBehaviorDecision =
  | { kind: "behavior"; behavior: Behavior; targetHeading?: number }
  | { kind: "pose"; toPose: CatPose; behavior: Behavior }
  | { kind: "turn"; targetHeading: number; behavior: Behavior };

export interface AIContext {
  currentPose: CatPose;
  current: Behavior;
  position: THREE.Vector3;
}

export interface CatAnimatorOpts {
  model: THREE.Group;
  mixer: THREE.AnimationMixer;
  actions: Map<string, THREE.AnimationAction>;
  pool: Behavior[];
  capsules: CatBoneCapsules;
  world: SceneWorld;
  /** Called when the current behavior's duration expires — only when AI
   * is enabled (the debug HUD can pause the picker). The animator
   * routes the result through the appropriate trigger. */
  pickNext: (ctx: AIContext) => NextBehaviorDecision;
  /** Called when locomotion would step out of the roam zone. The
   * animator clamps to the bound, then routes the policy's choice. */
  pickRedirect: (ctx: AIContext) => NextBehaviorDecision;
}

export interface CatAnimator {
  /** The cat's root group. Owned by the animator (it manages position,
   * rotation, and visibility). */
  model: THREE.Group;
  mode: () => Mode;
  isBusy: () => boolean;
  currentPose: () => CatPose;
  currentBehavior: () => Behavior;
  currentClipName: () => string;
  transitionProgress: () => number | null;

  /** Trigger a clip by name — handles Turn clips, pose-transition
   * clips, and regular behaviors. Routes through pose transitions when
   * needed. Logs and returns if the cat is busy mid-transition / turn. */
  triggerBehaviorByClip: (clip: string) => void;
  /** Move the cat to the given pose, settling into the pose's idle
   * clip. No-op if already there or if busy. */
  transitionToPose: (pose: CatPose) => void;
  /** Cancel any in-flight turn / pose-transition and snap back to
   * "playing" mode. */
  cancelInflight: () => void;

  setVisible: (v: boolean) => void;
  setAIEnabled: (enabled: boolean) => void;
  setAnimSpeed: (mul: number) => void;
  setWaypoint: (target: THREE.Vector3 | null) => void;
  setKeyInput: (input: CatKeyInput) => void;
}

export function createCatAnimator(opts: CatAnimatorOpts): CatAnimator {
  const { model, mixer, actions, pool, capsules, world } = opts;

  // Initial pose: a few meters out from the shelf, x-centered, facing
  // +X. Spawning right against the shelf would punch the kinematic
  // collider into the (now dynamic) shelf body and catapult it.
  let heading = INITIAL_HEADING;
  model.position.set(0, FLOOR_Y, 5);
  model.rotation.y = heading;

  // Teleport every bone-capsule body to its bone's current world pose
  // so they don't sweep dynamic bodies on startup.
  model.updateMatrixWorld(true);
  capsules.sync(true);

  let mode: Mode = "playing";
  let currentPose: CatPose = "stand";

  // Debug-driven controls. `enabled` gates the autonomous behavior
  // picker; `speed` scales the per-frame dt inside this module's
  // world.onUpdate callback so physics / scene continue at real time
  // while the cat slows / pauses.
  const debugSt = {
    enabled: true,
    speed: 1,
  };

  // Legal mode transitions. Everything routes through "playing" — there
  // are no direct turning↔transitioning_pose or controlled↔turning paths.
  // Same-mode is allowed (replaying a behavior, re-entering controlled
  // while inputs are still held). setMode warns on illegal transitions so
  // future contributors don't silently bypass the cancelInflight() route.
  const LEGAL_MODE_TRANSITIONS: Record<Mode, readonly Mode[]> = {
    playing: ["playing", "turning", "transitioning_pose", "controlled"],
    turning: ["playing"],
    transitioning_pose: ["playing", "transitioning_pose"], // queued clip
    controlled: ["playing"],
  };
  function setMode(next: Mode): void {
    if (!LEGAL_MODE_TRANSITIONS[mode].includes(next)) {
      console.warn(`[cupilot] cat: illegal mode transition ${mode} → ${next}`);
    }
    mode = next;
  }

  // Player-control state. `waypoint` is set by right-click; `keyInput`
  // tracks held WASD-style keys. When either is active, `mode` flips to
  // "controlled" and the autonomous AI sleeps until inputs are released
  // (and the waypoint, if any, has been reached).
  let waypoint: THREE.Vector3 | null = null;
  const keyInput: CatKeyInput = {
    forward: false,
    backward: false,
    left: false,
    right: false,
    run: false,
  };

  // Controlled-mode turn state. We play one Turn-90/45 clip on the
  // rising edge of an A/D press or when a waypoint is freshly set with
  // a heading delta > SMALL_TURN, then hand off to smooth rotation if
  // the input stays held. `elapsed = Infinity` is the inactive
  // sentinel; switches to 0 when a Turn clip starts and ticks up until
  // duration. Keeping the sentinel lets the post-turn ramp logic share
  // the elapsed/duration shape.
  const ctrlSt = {
    elapsed: Infinity,
    duration: 0,
    angle: 0, // signed radians applied at the end of the clip
    prevDir: 0 as 1 | -1 | 0,
  };
  function hasActiveKey(): boolean {
    return (
      keyInput.forward || keyInput.backward || keyInput.left || keyInput.right
    );
  }
  function isControlled(): boolean {
    return waypoint !== null || hasActiveKey();
  }

  let current: Behavior = pickWeighted(pool, currentPose, null);
  let action: THREE.AnimationAction = startAction(current);
  let timer = 0;
  let duration = behaviorDuration(current, action);

  // Speed-ramp state. Locomotion clips have very different paces (Walk
  // 0.58, Run 3.88); jumping speed mid-crossfade leaves the legs
  // blending a walk while the body lurches. Smoothing `speed` over FADE
  // syncs. `fadeT` ticks from 0 → FADE during a crossfade, then sticks
  // at FADE (idle); the prev/current speed lerp uses fadeT/FADE.
  const rampSt = {
    prev: 0,
    fadeT: FADE,
  };

  // Turn state. Active when mode === "turning"; the post-turn ramp
  // (postElapsed < FADE) is a sub-state that runs after mode flips back
  // to "playing" so the visible rotation hand-off lerps cleanly.
  const turnSt = {
    delta: 0,
    elapsed: 0,
    duration: 0,
    next: null as Behavior | null,
    postElapsed: Infinity,
    postFromY: 0,
    postToY: 0,
  };

  // Pose-transition state. Active when mode === "transitioning_pose";
  // `queue` holds the chained clip names (e.g. Sit_from → Lie_to for
  // sit → lie via stand); the head clip is currently playing.
  const poseSt = {
    queue: [] as string[],
    elapsed: 0,
    duration: 0,
    target: "stand" as CatPose,
    thenPlay: null as Behavior | null,
  };

  // ─── Post-turn ramp helpers ─────────────────────────────────────────
  //
  // After a Turn clip finishes, the cat's `model.rotation.y` is at the
  // pre-clip heading (we held it there while the clip's authored Root
  // rotation did the visible pivot). The next clip's fade-in still has
  // the Turn clip's Root contribution dropping toward zero, which would
  // otherwise un-rotate the cat back to the pre-clip heading. The ramp
  // lerps `heading` → `postToY` over FADE so the visible orientation
  // stays glued to the new heading the whole window.

  function startPostTurnRamp(targetHeading: number): void {
    turnSt.postFromY = heading;
    turnSt.postToY = wrapAngle(targetHeading);
    turnSt.postElapsed = 0;
  }

  /** Advance the ramp by `dt`. Returns true while the ramp is in
   * progress (so the caller can early-return without applying further
   * locomotion / yaw). Snaps to the end when it crosses FADE. */
  function tickPostTurnRamp(dt: number): boolean {
    if (turnSt.postElapsed >= FADE) return false;
    turnSt.postElapsed += dt;
    const t = Math.min(1, turnSt.postElapsed / FADE);
    const delta = wrapAngle(turnSt.postToY - turnSt.postFromY);
    heading = turnSt.postFromY + delta * t;
    if (t >= 1) {
      heading = turnSt.postToY;
      turnSt.postElapsed = Infinity;
    }
    model.rotation.y = heading;
    return true;
  }

  /** Snap to the ramp's end immediately (used by cancelInflight + new-
   * turn / pose-transition pre-rolls). No-op when the ramp isn't active. */
  function cancelPostTurnRampSnap(): void {
    if (turnSt.postElapsed >= FADE) return;
    heading = turnSt.postToY;
    model.rotation.y = heading;
    turnSt.postElapsed = Infinity;
  }

  function startAction(b: Behavior): THREE.AnimationAction {
    const a = actions.get(b.clip)!;
    a.reset().fadeIn(FADE).play();
    return a;
  }

  function startTurn(target: number, after: Behavior): boolean {
    const delta = wrapAngle(target - heading);
    if (Math.abs(delta) < SMALL_TURN) {
      heading = target;
      model.rotation.y = heading;
      return false;
    }
    const isLeft = delta > 0;
    const big = Math.abs(delta) > MEDIUM_TURN;
    const clipName = big
      ? isLeft
        ? "Turn-90_L"
        : "Turn-90_R"
      : isLeft
        ? "Turn-45_L"
        : "Turn-45_R";
    const clipDelta = (big ? Math.PI / 2 : Math.PI / 4) * (isLeft ? 1 : -1);
    const turnAction = actions.get(clipName);
    if (!turnAction) {
      heading = target;
      model.rotation.y = heading;
      return false;
    }
    turnAction.reset().fadeIn(FADE).play();
    if (action !== turnAction) action.fadeOut(FADE);
    action = turnAction;

    cancelPostTurnRampSnap();
    setMode("turning");
    turnSt.delta = clipDelta;
    turnSt.elapsed = 0;
    turnSt.duration = turnAction.getClip().duration;
    turnSt.next = after;
    return true;
  }

  function transition(next: Behavior, targetHeading?: number): void {
    if (
      next.speed !== 0 &&
      targetHeading !== undefined &&
      startTurn(targetHeading, next)
    ) {
      return;
    }
    if (next === current && action === actions.get(next.clip)) {
      action.reset().play();
    } else {
      const incoming = actions.get(next.clip)!;
      const outgoing = action;
      if (incoming !== outgoing) {
        incoming.reset().fadeIn(FADE).play();
        outgoing.fadeOut(FADE);
      }
      action = incoming;
    }
    rampSt.prev = current.speed;
    rampSt.fadeT = 0;
    current = next;
    setMode("playing");
    timer = 0;
    duration = behaviorDuration(next, action);
  }

  function startPoseTransition(
    toPose: CatPose,
    thenPlay: Behavior | null,
  ): void {
    const route = planTransitions(currentPose, toPose);
    if (route.length === 0) {
      // Already there — just play (or settle into idle).
      if (thenPlay) {
        transition(thenPlay);
      }
      return;
    }
    // Cancel any in-flight turn / post-turn ramp first so we have a
    // clean baseline.
    if (mode === "turning") {
      action.stop();
      turnSt.next = null;
    }
    cancelPostTurnRampSnap();
    poseSt.queue = route.slice(1);
    poseSt.target = toPose;
    poseSt.thenPlay = thenPlay;
    playTransitionClip(route[0]);
  }

  function playTransitionClip(clipName: string): void {
    const transitionAction = actions.get(clipName);
    if (!transitionAction) {
      // Clip missing — skip and advance.
      if (poseSt.queue.length > 0) {
        playTransitionClip(poseSt.queue.shift()!);
      } else {
        finishPoseTransition();
      }
      return;
    }
    transitionAction.reset().fadeIn(FADE).play();
    if (action !== transitionAction) action.fadeOut(FADE);
    action = transitionAction;
    setMode("transitioning_pose");
    poseSt.elapsed = 0;
    poseSt.duration = transitionAction.getClip().duration;
  }

  function finishPoseTransition(): void {
    currentPose = poseSt.target;
    const toPlay = poseSt.thenPlay;
    poseSt.thenPlay = null;
    if (toPlay) {
      transition(toPlay);
    } else {
      // Settle into the new pose's idle.
      const idleBehavior = pool.find((b) => b.clip === POSE_IDLE[currentPose]);
      if (idleBehavior) {
        transition(idleBehavior);
      } else {
        setMode("playing");
        timer = 0;
        duration = 0;
      }
    }
  }

  function cancelInflight(): void {
    if (mode === "turning") {
      action.stop();
      turnSt.next = null;
    }
    if (mode === "transitioning_pose") {
      action.stop();
      poseSt.queue = [];
      poseSt.thenPlay = null;
    }
    cancelPostTurnRampSnap();
    setMode("playing");
  }

  function enterControlled(): void {
    if (mode === "controlled") return;
    // Cancel any in-flight turn / pose transition / behavior so we
    // have a clean baseline.
    cancelInflight();
    if (currentPose !== "stand") {
      // Play the proper wake-up sequence (e.g. sleep → lie → stand)
      // before honoring the player's input, otherwise the cat would
      // snap from sleep pose to walking. The dispatcher in onUpdate
      // flips to controlled mode the frame after the transition
      // completes — see the `mode === "playing" && isControlled()`
      // hand-off there. Player input (waypoint, keys) stays set on
      // the closure during the transition and applies the moment we
      // flip.
      startPoseTransition("stand", null);
      return;
    }
    setMode("controlled");
  }

  function exitControlled(): void {
    if (mode !== "controlled") return;
    // transition() resets mode to "playing" and refreshes the AI timer.
    const idle = pool.find((b) => b.clip === POSE_IDLE.stand) ?? pool[0];
    transition(idle);
  }

  function swapToClip(target: Behavior): void {
    if (current === target && action === actions.get(target.clip)) return;
    const incoming = actions.get(target.clip)!;
    if (incoming !== action) {
      incoming.reset().fadeIn(FADE).play();
      action.fadeOut(FADE);
    }
    rampSt.prev = current.speed;
    rampSt.fadeT = 0;
    action = incoming;
    current = target;
  }

  function startCtrlTurnInPlace(direction: 1 | -1, prefer45: boolean): void {
    // Pick the smaller Turn clip when the desired heading change is
    // close (waypoint steering with a moderate delta), otherwise
    // prefer Turn-90. Falls through to whichever clip is available.
    const big = direction === 1 ? "Turn-90_L" : "Turn-90_R";
    const small = direction === 1 ? "Turn-45_L" : "Turn-45_R";
    let turnAction: THREE.AnimationAction | undefined;
    if (prefer45) {
      turnAction = actions.get(small) ?? actions.get(big);
    } else {
      turnAction = actions.get(big) ?? actions.get(small);
    }
    if (!turnAction) return;
    turnAction.reset().fadeIn(FADE).play();
    if (action !== turnAction) action.fadeOut(FADE);
    action = turnAction;
    const usingBig = turnAction === actions.get(big);
    ctrlSt.duration = turnAction.getClip().duration;
    ctrlSt.angle = (usingBig ? Math.PI / 2 : Math.PI / 4) * direction;
    ctrlSt.elapsed = 0;
  }

  function endCtrlTurnIntoRamp(): void {
    // End the current Turn clip and queue a post-turn heading ramp so
    // the swap to the next action stays visually continuous. Mirrors
    // the autonomous AI's startTurn / post-turn dance.
    if (ctrlSt.elapsed === Infinity) return;
    const progress = Math.min(1, ctrlSt.elapsed / ctrlSt.duration);
    startPostTurnRamp(heading + ctrlSt.angle * progress);
    ctrlSt.elapsed = Infinity;
  }

  function updateControlled(dt: number): void {
    // 1) Decide if a Turn clip is wanted right now.
    //    - A or D held → turn in that direction.
    //    - Otherwise, if a waypoint is set with heading delta beyond
    //      WAYPOINT_TURN_THRESHOLD (30°), turn toward it. Below that,
    //      smooth steering handles the fine adjustment.
    let wantsTurnDir: 1 | -1 | 0 = 0;
    let prefer45 = false;
    if (keyInput.left !== keyInput.right) {
      wantsTurnDir = keyInput.left ? 1 : -1;
    } else if (waypoint !== null) {
      const dx = waypoint.x - model.position.x;
      const dz = waypoint.z - model.position.z;
      const dist = Math.hypot(dx, dz);
      if (dist >= WAYPOINT_ARRIVE_RADIUS) {
        const targetHeading = Math.atan2(dx, dz);
        const dh = wrapAngle(targetHeading - heading);
        if (Math.abs(dh) > WAYPOINT_TURN_THRESHOLD) {
          wantsTurnDir = dh > 0 ? 1 : -1;
          prefer45 = Math.abs(dh) <= MEDIUM_TURN;
        }
      }
    }

    // 2) On the rising edge of a turn intent kick off a single Turn
    //    clip. Holding the same direction afterwards continues with
    //    smooth rotation rather than chaining clips.
    const inFlight = ctrlSt.elapsed !== Infinity;
    const inFlightDir: 1 | -1 | 0 = inFlight ? (ctrlSt.angle >= 0 ? 1 : -1) : 0;
    if (wantsTurnDir !== 0 && wantsTurnDir !== ctrlSt.prevDir) {
      if (inFlight && inFlightDir !== wantsTurnDir) {
        // Flip direction mid-clip — bake what we've turned so far
        // into a post-turn ramp so the swap stays smooth.
        endCtrlTurnIntoRamp();
      }
      if (!inFlight || inFlightDir !== wantsTurnDir) {
        startCtrlTurnInPlace(wantsTurnDir, prefer45);
      }
    }
    ctrlSt.prevDir = wantsTurnDir;

    // 3) Turn clip in flight: hold heading, count down. When it ends,
    //    queue a post-turn ramp and fall through.
    if (ctrlSt.elapsed !== Infinity) {
      ctrlSt.elapsed += dt;
      if (ctrlSt.elapsed >= ctrlSt.duration) {
        startPostTurnRamp(heading + ctrlSt.angle);
        ctrlSt.elapsed = Infinity;
      } else {
        model.rotation.y = heading;
        return;
      }
    }

    // 4) Post-turn ramp.
    if (tickPostTurnRamp(dt)) {
      // Kick the action crossfade on the first ramp frame (and any
      // later frames if the desired clip changed). Locomotion picks
      // the clip; backward (S) treats stand idle as the rest.
      let desiredClip = POSE_IDLE.stand;
      if (keyInput.forward) desiredClip = keyInput.run ? "Run_F" : "Walk_F";
      const desiredAction = actions.get(desiredClip);
      if (desiredAction && action !== desiredAction) {
        const beh = pool.find((b) => b.clip === desiredClip);
        if (beh) swapToClip(beh);
      }
      return;
    }

    // 5) Continuous yaw from A/D — only after the Turn clip + ramp
    //    cycle is done.
    if (keyInput.left) heading = wrapAngle(heading + KEY_TURN_RATE * dt);
    if (keyInput.right) heading = wrapAngle(heading - KEY_TURN_RATE * dt);

    // Decide whether to move and which clip + speed to use.
    let desiredClip = POSE_IDLE.stand;
    let signedSpeedScale = 0;

    if (keyInput.forward) {
      desiredClip = keyInput.run ? "Run_F" : "Walk_F";
      signedSpeedScale = 1;
    } else if (keyInput.backward) {
      // No reverse-walk clip in the pack — treat S as "stop." We still
      // play the stand idle so the cat looks alert rather than mid-stride.
      desiredClip = POSE_IDLE.stand;
      signedSpeedScale = 0;
    } else if (waypoint !== null) {
      const dx = waypoint.x - model.position.x;
      const dz = waypoint.z - model.position.z;
      const dist = Math.hypot(dx, dz);
      if (dist < WAYPOINT_ARRIVE_RADIUS) {
        // Arrived — clear the waypoint. If no keys are held the next
        // frame's `isControlled()` check will flip us back to AI.
        waypoint = null;
        desiredClip = POSE_IDLE.stand;
        signedSpeedScale = 0;
      } else {
        const targetHeading = Math.atan2(dx, dz);
        const dh = wrapAngle(targetHeading - heading);
        const step = Math.sign(dh) * Math.min(Math.abs(dh), KEY_TURN_RATE * dt);
        heading = wrapAngle(heading + step);
        if (keyInput.run) {
          desiredClip = "Run_F";
        } else {
          desiredClip = dist > WAYPOINT_TROT_DISTANCE ? "Trot_F" : "Walk_F";
        }
        signedSpeedScale = 1;
      }
    }

    model.rotation.y = heading;

    // Swap if the *behavior* changed (e.g. Walk → Trot) OR if the
    // active mixer action is out of sync with the desired clip.
    const desiredAction = actions.get(desiredClip);
    if (
      desiredAction &&
      (current.clip !== desiredClip || action !== desiredAction)
    ) {
      const beh = pool.find((b) => b.clip === desiredClip);
      if (beh) swapToClip(beh);
    }

    // Apply the existing speed-ramp so transitions don't lurch.
    let effectiveSpeed = current.speed;
    if (rampSt.fadeT < FADE) {
      rampSt.fadeT += dt;
      const t = Math.min(1, rampSt.fadeT / FADE);
      effectiveSpeed = rampSt.prev + (current.speed - rampSt.prev) * t;
    }

    if (signedSpeedScale !== 0 && effectiveSpeed !== 0) {
      model.position.x +=
        signedSpeedScale * effectiveSpeed * dt * Math.sin(heading);
      model.position.z +=
        signedSpeedScale * effectiveSpeed * dt * Math.cos(heading);
      if (outOfBounds(model.position)) clampToBounds(model.position);
    }
  }

  // Per-frame mode steppers. Each reads/writes the surrounding closure
  // state directly. updateTurning returns true when the Turn clip just
  // ended this frame, signalling the dispatcher to fall through into
  // updatePlaying — locomotion + the post-turn ramp must start the same
  // frame the Turn clip ends, otherwise there's a one-frame visible
  // double-rotation between turn end and locomotion start.
  function updateTurning(dt: number): boolean {
    turnSt.elapsed += dt;
    if (turnSt.elapsed < turnSt.duration) return false;
    const after = turnSt.next!;
    turnSt.next = null;
    const incoming = actions.get(after.clip)!;
    incoming.reset().fadeIn(FADE).play();
    action.fadeOut(FADE);
    startPostTurnRamp(heading + turnSt.delta);
    rampSt.prev = 0;
    rampSt.fadeT = 0;
    current = after;
    action = incoming;
    setMode("playing");
    timer = 0;
    duration = behaviorDuration(after, action);
    return true;
  }

  function updateTransitioningPose(dt: number): void {
    poseSt.elapsed += dt;
    if (poseSt.elapsed < poseSt.duration) return;
    if (poseSt.queue.length > 0) {
      playTransitionClip(poseSt.queue.shift()!);
    } else {
      finishPoseTransition();
    }
  }

  function applyDecision(decision: NextBehaviorDecision): void {
    switch (decision.kind) {
      case "behavior":
        transition(decision.behavior, decision.targetHeading);
        return;
      case "pose":
        startPoseTransition(decision.toPose, decision.behavior);
        return;
      case "turn":
        startTurn(decision.targetHeading, decision.behavior);
        return;
    }
  }

  function aiContext(): AIContext {
    return { currentPose, current, position: model.position };
  }

  function updatePlaying(dt: number): void {
    // Post-turn heading ramp (if active) — interpolates rotation as the
    // outgoing Turn clip fades, cancelling its visible un-rotation.
    if (tickPostTurnRamp(dt)) {
      // Hold off on translation while the heading ramp is in flight.
      // Without this the cat stair-steps out of bounds at a corner: the
      // turn clip swings through 90° instead of the actual delta the AI
      // wanted, the post-ramp interpolates between the pre- and post-
      // clip headings, and any intermediate angle whose sin/cos points
      // at the wall pushes the cat back across the bound.
      capsules.sync();
      return;
    }

    let effectiveSpeed = current.speed;
    if (rampSt.fadeT < FADE) {
      rampSt.fadeT += dt;
      const t = Math.min(1, rampSt.fadeT / FADE);
      effectiveSpeed = rampSt.prev + (current.speed - rampSt.prev) * t;
    }

    if (effectiveSpeed !== 0) {
      model.position.x += effectiveSpeed * dt * Math.sin(heading);
      model.position.z += effectiveSpeed * dt * Math.cos(heading);
      if (outOfBounds(model.position)) {
        clampToBounds(model.position);
        if (debugSt.enabled) {
          // Pick a fresh behavior heading back into the zone. With AI
          // disabled we just clamp and continue — the user will be
          // driving the cat manually.
          applyDecision(opts.pickRedirect(aiContext()));
          capsules.sync();
          return;
        }
      }
    }

    timer += dt;
    if (debugSt.enabled && timer >= duration) {
      applyDecision(opts.pickNext(aiContext()));
    }

    capsules.sync();
  }

  // The world.onUpdate dispatcher.
  world.onUpdate((rawDt) => {
    if (!model.visible) return;
    // Scale dt for everything cat-related so the debug speed slider and
    // setAnimSpeed setter can slo-mo or pause the cat without affecting
    // physics, scene, or other systems that read the unscaled rawDt
    // from their own world.onUpdate subscriptions.
    const dt = rawDt * debugSt.speed;
    if (dt === 0) return;
    mixer.update(dt);

    // Honor control inputs the moment they're set — including frames
    // immediately after a wake-up pose transition completes. Without
    // this hand-off, a click that arrives while the cat is sleeping /
    // sitting kicks off the wake-up via enterControlled() but never
    // flips into controlled mode (because enterControlled only runs on
    // the falling→rising edge of isControlled()).
    if (mode === "playing" && isControlled()) {
      enterControlled();
    }

    if (mode === "controlled") {
      updateControlled(dt);
      // Inputs may have all cleared mid-frame (e.g., the last held key
      // released and the waypoint was already null). Hand control back
      // to the AI cleanly.
      if (!isControlled()) exitControlled();
      capsules.sync();
      return;
    }
    if (mode === "transitioning_pose") {
      updateTransitioningPose(dt);
      return;
    }
    if (mode === "turning" && !updateTurning(dt)) return;
    updatePlaying(dt);
  });

  // ─── Public API ────────────────────────────────────────────────────

  function isBusy(): boolean {
    return mode === "transitioning_pose" || mode === "turning";
  }

  function transitionProgress(): number | null {
    if (mode === "transitioning_pose" && poseSt.duration > 0) {
      return Math.min(1, poseSt.elapsed / poseSt.duration);
    }
    if (mode === "turning" && turnSt.duration > 0) {
      return Math.min(1, turnSt.elapsed / turnSt.duration);
    }
    return null;
  }

  function transitionToPose(pose: CatPose): void {
    if (pose === currentPose && mode === "playing") return;
    // Guardrail: never interrupt a transition / turn mid-clip — the
    // partial pose snapshot at cancelInflight time produces a visibly
    // broken animation when the next clip fades in. Reject; the HUD
    // disables triggers via isBusy() but external callers (dev API,
    // tests) bypass that gate.
    if (mode === "transitioning_pose" || mode === "turning") {
      console.warn(
        `[cupilot] cat: transitionToPose(${pose}) ignored — busy (${mode})`,
      );
      return;
    }
    cancelInflight();
    startPoseTransition(pose, null);
  }

  function triggerBehaviorByClip(clip: string): void {
    if (mode === "transitioning_pose" || mode === "turning") {
      console.warn(
        `[cupilot] cat: triggerBehavior(${clip}) ignored — busy (${mode})`,
      );
      return;
    }
    // Turn clips: play a turn that returns to Base.
    if (TURN_CLIPS.includes(clip) && actions.has(clip)) {
      const isLeft = clip.endsWith("_L");
      const big = clip.startsWith("Turn-90");
      const delta = (big ? Math.PI / 2 : Math.PI / 4) * (isLeft ? 1 : -1);
      const baseBehavior = pool.find((b) => b.clip === "Base") ?? pool[0];
      cancelInflight();
      // Turning is stand-only — if we're elsewhere, get to stand first.
      if (currentPose !== "stand") {
        startPoseTransition("stand", baseBehavior);
        return;
      }
      startTurn(wrapAngle(heading + delta), baseBehavior);
      return;
    }

    // Pose transition clips: trigger that specific transition.
    const transitionEdge = (
      Object.entries(POSE_TRANSITIONS) as Array<[string, string | undefined]>
    ).find(([, c]) => c === clip);
    if (transitionEdge) {
      const [edge] = transitionEdge;
      const arrowIdx = edge.indexOf("->");
      const to = edge.slice(arrowIdx + 2) as CatPose;
      cancelInflight();
      // Force currentPose to the source side of this edge so the
      // transition fires even if state thinks we're already at `to`.
      const from = edge.slice(0, arrowIdx) as CatPose;
      currentPose = from;
      startPoseTransition(to, null);
      return;
    }

    // Behavior clips.
    const target = pool.find((b) => b.clip === clip);
    if (!target) {
      console.warn(`[cupilot] cat: no behavior for clip "${clip}"`);
      return;
    }
    cancelInflight();
    if (target.pose !== currentPose) {
      startPoseTransition(target.pose, target);
    } else if (target === current && mode === "playing") {
      action.reset().play();
      timer = 0;
      duration = behaviorDuration(target, action);
    } else {
      transition(target);
    }
  }

  return {
    model,
    mode: () => mode,
    isBusy,
    currentPose: () => currentPose,
    currentBehavior: () => current,
    currentClipName: () => action.getClip().name,
    transitionProgress,

    triggerBehaviorByClip,
    transitionToPose,
    cancelInflight,

    setVisible: (v) => {
      model.visible = v;
      // When the cat is hidden (focus mode) we also disable its
      // collider so a focused cup that drifts through the cat's
      // resting spot isn't shoved by a phantom kinematic. On show,
      // teleport the body back to the model's current pose before
      // re-enabling so reactivation doesn't sweep through anything in
      // between.
      if (v) {
        capsules.sync(true);
        capsules.setEnabled(true);
      } else {
        capsules.setEnabled(false);
      }
    },
    setAIEnabled: (enabled) => {
      debugSt.enabled = enabled;
    },
    setAnimSpeed: (mul) => {
      debugSt.speed = Math.max(0, mul);
    },
    setWaypoint: (target) => {
      if (target === null) {
        waypoint = null;
      } else {
        // Clamp to the cat's roam zone so a click on the shelf or far
        // off the floor still resolves to a reachable spot.
        waypoint = new THREE.Vector3(
          Math.max(X_MIN, Math.min(X_MAX, target.x)),
          FLOOR_Y,
          Math.max(Z_MIN, Math.min(Z_MAX, target.z)),
        );
      }
      // Guardrail: don't yank the cat out of a transition / turn mid-
      // clip. Input stays on the closure; the dispatcher picks it up
      // the next time mode flips back to "playing".
      if (mode === "transitioning_pose" || mode === "turning") return;
      if (isControlled()) enterControlled();
      else if (mode === "controlled") exitControlled();
    },
    setKeyInput: (input) => {
      keyInput.forward = input.forward;
      keyInput.backward = input.backward;
      keyInput.left = input.left;
      keyInput.right = input.right;
      keyInput.run = input.run;
      if (mode === "transitioning_pose" || mode === "turning") return;
      if (isControlled()) enterControlled();
      else if (mode === "controlled") exitControlled();
    },
  };
}
